import React, { useState, useEffect, useRef } from "react";
import {
  getChatHistory,
  sendMessage as sendChatMessage,
} from "../../../services/AdminPortal/chatAPI";
import io from "socket.io-client";
import { toast } from "react-toastify";
import { Send } from "lucide-react";

const Chat = ({
  staffUserId,
  customerId,
  senderType = "Staff",
  receiverType = "Customer",
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const messagesContainerRef = useRef(null);
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || "https://dashboard.lono.co.in";
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 3000;
  const MAX_CHAR_LIMIT = 300;

  const connectSocket = (attempt = 0) => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (!staffUserId || !customerId) return;

    try {
      const token =
        localStorage.getItem("adminToken") ||
        localStorage.getItem("staffToken") ||
        localStorage.getItem("customerToken") ||
        localStorage.getItem("bankingPartnerToken") ||
        localStorage.getItem("affiliatePartnerToken") ||
        localStorage.getItem("creditManagerToken") ||
        localStorage.getItem("token");

      socketRef.current = io(SOCKET_URL, {
        auth: { token },
        query: { token },
        transports: ["websocket", "polling"],
        reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
        reconnectionDelay: RECONNECT_DELAY,
        timeout: 10000,
      });

      socketRef.current.on("connect", () => {
        setIsConnected(true);
        setConnectionError(false);
        socketRef.current.emit("joinChat", { staffUserId, customerId });
      });

      socketRef.current.on("connect_error", () => {
        setIsConnected(false);
        setConnectionError(true);
        if (attempt < MAX_RECONNECT_ATTEMPTS) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectSocket(attempt + 1);
          }, RECONNECT_DELAY);
        } else {
          toast.error("Could not connect to chat server.");
        }
      });

      socketRef.current.on("disconnect", () => {
        setIsConnected(false);
      });

      socketRef.current.on("newMessage", (newMessage) => {
        setMessages((prev) => [...prev, newMessage]);
      });

      socketRef.current.on("messageError", (error) => {
        toast.error(`Chat error: ${error}`);
      });
    } catch {
      setIsConnected(false);
      setConnectionError(true);
    }
  };

  const fetchChatHistory = async () => {
    if (!staffUserId || !customerId) return;
    try {
      const data = await getChatHistory({
        user1Id: staffUserId,
        user1Type: senderType,
        user2Id: customerId,
        user2Type: receiverType,
      });
      setMessages(data?.chatHistory || []);
    } catch {
      toast.error("Could not load chat history");
      setMessages([]);
    }
  };

  useEffect(() => {
    fetchChatHistory();
    connectSocket();
    return () => {
      socketRef.current?.disconnect();
      if (reconnectTimeoutRef.current)
        clearTimeout(reconnectTimeoutRef.current);
    };
  }, [staffUserId, customerId]);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (input.trim() === "" || input.length > MAX_CHAR_LIMIT) return;

    const optimisticMessage = {
      senderId: staffUserId,
      senderType,
      receiverId: customerId,
      receiverType,
      message: input.trim(),
      createdAt: new Date().toISOString(),
    };

    // Optimistic UI update
    setMessages((prev) => [...prev, optimisticMessage]);

    try {
      await sendChatMessage(
        { receiverId: customerId, receiverType },
        input.trim()
      );
      setInput("");
      // Refresh history to sync with server state
      fetchChatHistory();
    } catch (err) {
      toast.error(`Failed to send message: ${err?.message || err}`);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_CHAR_LIMIT) {
      setInput(value);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };

  const handleRetryConnection = () => {
    connectSocket();
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="bg-[#1e1e1e] rounded-lg p-4 flex flex-col h-[500px]">
      {connectionError && (
        <div className="bg-red-500 bg-opacity-20 text-red-400 p-2 rounded mb-2 flex justify-between items-center">
          <span>Chat connection lost</span>
          <button
            onClick={handleRetryConnection}
            className="bg-red-500 text-white px-2 py-1 rounded text-xs"
          >
            Retry
          </button>
        </div>
      )}

      <div
        ref={messagesContainerRef}
        className="flex-1 min-h-0 overflow-y-auto border border-[#333333] rounded p-2 mb-2
        scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-transparent"
      >
        {messages.length === 0 && (
          <p className="text-gray-400 text-sm text-center">
            Start chatting with the customer...
          </p>
        )}
        <div className="flex flex-col space-y-4 w-full">
          {messages.map((msg, index) => {
            const isStaff = msg.senderType === "Staff";
            return (
              <div
                key={index}
                className={`flex flex-col ${
                  isStaff ? "items-end" : "items-start"
                } w-full`}
              >
                <div
                  className={`px-4 py-2 rounded-lg max-w-[80%] whitespace-pre-wrap break-words ${
                    isStaff
                      ? "bg-[#FFCA20] text-black rounded-tr-none"
                      : "bg-[#2c2c2c] text-white rounded-tl-none"
                  }`}
                >
                  {msg.message}
                </div>
                <div
                  className={`text-xs text-gray-400 mt-1 ${
                    isStaff ? "pr-2" : "pl-2"
                  }`}
                >
                  {formatTime(msg.createdAt)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="text-right text-xs text-red-400 mb-1">
        {input.length}/{MAX_CHAR_LIMIT}
      </div>

      <div className="flex">
        <textarea
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={isConnected ? "Type your message..." : "Connecting..."}
          disabled={!isConnected}
          rows={3}
          className="flex-grow rounded-l resize-none px-3 py-2 bg-gray-800 text-white border border-[#333333]
            focus:outline-none disabled:bg-gray-700 disabled:text-gray-500
            scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-transparent"
        />
        <button
          onClick={handleSend}
          disabled={!isConnected || input.length > MAX_CHAR_LIMIT}
          className="bg-[#FFCA20] text-black px-4 py-2 rounded-r font-semibold hover:bg-yellow-600 disabled:bg-gray-700 disabled:text-gray-500"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
};

export default Chat;
